import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'

/** Parsed underlying Token sub-struct */
export interface BenqiToken {
  token: string
  decimals: number
  symbol: string
  name: string
}

/**
 * Shared parsed shape for both regular BENQI and ecosystem MarketMetadata.
 * Fields that only exist on one variant are optional.
 */
export interface BenqiMarketMetadata {
  market: string // qiToken address
  supplyRate: bigint // per-block, 1e18 scaled
  borrowRate: bigint // per-block, 1e18 scaled
  price: bigint // 1e(36 - underlyingDecimals) scaled
  exchangeRate: bigint // 1e(18 - qiTokenDec + underlyingDec) scaled
  reserveFactor: bigint // 1e18 scaled
  borrowCap: bigint
  supplyCap: bigint // 0 for regular BENQI (no supply cap)
  totalSupply: bigint // qiToken supply
  totalUnderlyingSupply: bigint // underlying supply
  totalBorrows: bigint
  collateralFactor: bigint // 1e18 scaled
  underlying: BenqiToken
  qiTokenDecimals: number
  totalReserves: bigint
  cash: bigint
  mintPaused: boolean
  borrowPaused: boolean
}

function toBigInt(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') return BigInt(value)
  if (typeof value === 'string') return BigInt(value)
  if (value && typeof value === 'object') {
    const v = value as any
    if (typeof v._hex === 'string') return BigInt(v._hex)
    if (typeof v.toString === 'function') return BigInt(v.toString())
  }
  throw new Error(`Cannot convert value to bigint: ${String(value)}`)
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value !== 0n
  if (typeof value === 'number') return value !== 0
  return Boolean(value)
}

function field<T = unknown>(src: any, key: string, index: number): T {
  if (src == null) throw new Error('Source is null/undefined')
  if (key in src) return src[key]
  if (Array.isArray(src)) return src[index]
  return (src as any)[key]
}

function parseToken(raw: any): BenqiToken {
  return {
    token: String(field(raw, 'token', 0)),
    decimals: Number(field(raw, 'decimals', 1)),
    symbol: String(field(raw, 'symbol', 2)),
    name: String(field(raw, 'name', 3)),
  }
}

/**
 * Parse regular BENQI MarketMetadata struct.
 *
 * Struct order: market(0), supplyRate(1), borrowRate(2), price(3),
 * exchangeRate(4), reserveFactor(5), borrowCap(6), totalSupply(7),
 * totalUnderlyingSupply(8), totalBorrows(9), collateralFactor(10),
 * underlying(11), qiTokenDecimals(12), avaxSupplyRewardSpeed(13),
 * avaxBorrowRewardSpeed(14), qiSupplyRewardSpeed(15), qiBorrowRewardSpeed(16),
 * totalReserves(17), cash(18), mintPaused(19), borrowPaused(20)
 */
export function parseBenqiMarketMetadata(input: any): BenqiMarketMetadata {
  return {
    market: String(field(input, 'market', 0)),
    supplyRate: toBigInt(field(input, 'supplyRate', 1)),
    borrowRate: toBigInt(field(input, 'borrowRate', 2)),
    price: toBigInt(field(input, 'price', 3)),
    exchangeRate: toBigInt(field(input, 'exchangeRate', 4)),
    reserveFactor: toBigInt(field(input, 'reserveFactor', 5)),
    borrowCap: toBigInt(field(input, 'borrowCap', 6)),
    supplyCap: 0n, // regular BENQI has no supply cap
    totalSupply: toBigInt(field(input, 'totalSupply', 7)),
    totalUnderlyingSupply: toBigInt(field(input, 'totalUnderlyingSupply', 8)),
    totalBorrows: toBigInt(field(input, 'totalBorrows', 9)),
    collateralFactor: toBigInt(field(input, 'collateralFactor', 10)),
    underlying: parseToken(field(input, 'underlying', 11)),
    qiTokenDecimals: Number(field(input, 'qiTokenDecimals', 12)),
    totalReserves: toBigInt(field(input, 'totalReserves', 17)),
    cash: toBigInt(field(input, 'cash', 18)),
    mintPaused: toBool(field(input, 'mintPaused', 19)),
    borrowPaused: toBool(field(input, 'borrowPaused', 20)),
  }
}

/**
 * Parse ecosystem BENQI MarketMetadata struct.
 *
 * Struct order: market(0), supplyRate(1), borrowRate(2), price(3),
 * exchangeRate(4), reserveFactor(5), borrowCap(6), supplyCap(7),
 * totalSupply(8), totalUnderlyingSupply(9), totalBorrows(10),
 * collateralFactor(11), underlying(12), qiTokenDecimals(13),
 * totalReserves(14), cash(15), mintPaused(16), borrowPaused(17),
 * rewards(18)
 */
export function parseBenqiEcoMarketMetadata(input: any): BenqiMarketMetadata {
  return {
    market: String(field(input, 'market', 0)),
    supplyRate: toBigInt(field(input, 'supplyRate', 1)),
    borrowRate: toBigInt(field(input, 'borrowRate', 2)),
    price: toBigInt(field(input, 'price', 3)),
    exchangeRate: toBigInt(field(input, 'exchangeRate', 4)),
    reserveFactor: toBigInt(field(input, 'reserveFactor', 5)),
    borrowCap: toBigInt(field(input, 'borrowCap', 6)),
    supplyCap: toBigInt(field(input, 'supplyCap', 7)),
    totalSupply: toBigInt(field(input, 'totalSupply', 8)),
    totalUnderlyingSupply: toBigInt(field(input, 'totalUnderlyingSupply', 9)),
    totalBorrows: toBigInt(field(input, 'totalBorrows', 10)),
    collateralFactor: toBigInt(field(input, 'collateralFactor', 11)),
    underlying: parseToken(field(input, 'underlying', 12)),
    qiTokenDecimals: Number(field(input, 'qiTokenDecimals', 13)),
    totalReserves: toBigInt(field(input, 'totalReserves', 14)),
    cash: toBigInt(field(input, 'cash', 15)),
    mintPaused: toBool(field(input, 'mintPaused', 16)),
    borrowPaused: toBool(field(input, 'borrowPaused', 17)),
  }
}

/**
 * Returns deposit APR and borrow APR (percentages) for one BENQI market.
 * Rates are per-block; uses chain block time via TimeScale.BLOCK (same as Venus).
 */
export function getBenqiRates(
  info: BenqiMarketMetadata,
  chainId: string,
  unitSeconds?: number,
) {
  const supplyApy = calculateRateForCompoundType(
    info.supplyRate.toString(),
    chainId,
    TimeScale.BLOCK,
    unitSeconds,
  )
  const borrowApy = calculateRateForCompoundType(
    info.borrowRate.toString(),
    chainId,
    TimeScale.BLOCK,
    unitSeconds,
  )
  return {
    depositRate: apyToApr(supplyApy / 100) * 100,
    variableBorrowRate: apyToApr(borrowApy / 100) * 100,
  }
}
